class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
    this.setHeaderCartIconAccessibility();

    // Preserve keyboard focus restoration without selecting the cart icon after touch.
    document.addEventListener('keydown', () => { this.keyboardInteraction = true; });
    document.addEventListener('pointerdown', () => { this.keyboardInteraction = false; }, { passive: true });

    // Close on a completed touch, rather than waiting for a synthesized click on iOS.
    this.addEventListener('touchstart', (event) => {
      const target = event.target.closest('.drawer__close, #CartDrawer-Overlay');
      const touch = event.touches[0];
      this.closeTouch = target && event.touches.length === 1
        ? { target, x: touch.clientX, y: touch.clientY, id: touch.identifier }
        : null;
    }, { passive: true });
    this.addEventListener('touchcancel', () => { this.closeTouch = null; }, { passive: true });
    this.addEventListener('touchend', (event) => {
      const start = this.closeTouch;
      this.closeTouch = null;
      if (!start || !window.matchMedia('(max-width:749px)').matches) return;
      const touch = Array.from(event.changedTouches).find(item => item.identifier === start.id);
      const target = event.target.closest('.drawer__close, #CartDrawer-Overlay');
      if (!touch || target !== start.target ||
          Math.abs(touch.clientX - start.x) > 10 || Math.abs(touch.clientY - start.y) > 10) return;
      if (!event.cancelable) return;
      event.preventDefault(); // Suppress the subsequent click so it cannot reach the page behind.
      this.keyboardInteraction = false;
      this.close();
    }, { passive: false });
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    if (!cartLink) return;

    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink);
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  open(triggeredBy) {
    if (window.matchMedia('(max-width:749px)').matches) {
      this.mobileCycle = true;
      if (triggeredBy) this.setActiveElement(triggeredBy);
      cancelAnimationFrame(this.mobileOpenFrame);
      document.body.classList.add('overflow-hidden');
      // Begin the mobile drawer immediately; focus on the next frame without a fixed delay.
      this.classList.add('animate', 'active');
      this.mobileOpenFrame = requestAnimationFrame(() => {
        if (!this.classList.contains('active')) return;
        const panel = this.querySelector('.drawer__inner');
        if (panel) trapFocus(panel, panel);
      });
      return;
    }
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
    // here the animation doesn't seem to always get triggered. A timeout seem to help
    setTimeout(() => {
      this.classList.add('animate', 'active');
    });

    this.addEventListener(
      'transitionend',
      () => {
        const containerToTrapFocusOn = this.classList.contains('is-empty')
          ? this.querySelector('.drawer__inner-empty')
          : document.getElementById('CartDrawer');
        const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
        trapFocus(containerToTrapFocusOn, focusElement);
      },
      { once: true }
    );

    document.body.classList.add('overflow-hidden');
  }

  close() {
    if (this.pendingAdd) this.dismissedPendingAdd = true;
    if (this.mobileCycle) {
      cancelAnimationFrame(this.mobileOpenFrame);
      this.classList.remove('active');
      removeTrapFocus();
      document.body.classList.remove('overflow-hidden');
      if (this.keyboardInteraction && this.activeElement?.isConnected) {
        this.activeElement.focus({preventScroll:true});
      } else {
        const focused = document.activeElement;
        if (focused && (this.contains(focused) || focused === this.activeElement)) focused.blur();
        this.activeElement?.classList.remove('focused');
      }
      this.mobileCycle = false;
      return;
    }
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  beginPendingAdd() {
    if (!window.matchMedia('(max-width:749px)').matches) return;
    this.pendingAdd = true;
    this.dismissedPendingAdd = false;
    this.classList.add('ev-cart-pending');
    this.setAttribute('aria-busy', 'true');
    const panel = this.querySelector('.drawer__inner');
    if (panel && !panel.querySelector('.ev-cart-pending-message')) {
      const message = document.createElement('div');
      message.className = 'ev-cart-pending-message';
      message.setAttribute('role', 'status');
      message.textContent = document.documentElement.lang.startsWith('en')
        ? 'Adding product…' : 'Agregando producto…';
      panel.appendChild(message);
    }
    this.open();
  }

  finishPendingAdd() {
    this.pendingAdd = false;
    this.classList.remove('ev-cart-pending');
    this.removeAttribute('aria-busy');
    this.querySelector('.ev-cart-pending-message')?.remove();
  }

  cancelPendingAdd() {
    if (!this.pendingAdd) return;
    this.finishPendingAdd();
    this.close();
  }

  renderContents(parsedState) {
    const shouldOpen = !this.dismissedPendingAdd;
    this.finishPendingAdd();
    this.dismissedPendingAdd = false;
    // Validate every returned section before replacing any visible content.
    let updates;
    try {
      updates = this.getSectionsToRender().map((section) => {
        const target = section.selector
          ? document.querySelector(section.selector)
          : document.getElementById(section.id);
        const html = parsedState.sections?.[section.id];
        if (!target || typeof html !== 'string') throw new Error('Missing cart section');
        const source = this.getSectionDOM(html, section.selector);
        if (!source) throw new Error('Invalid cart section');
        return { target, html: source.innerHTML };
      });
    } catch (error) {
      // The addition succeeded. Show the server cart without submitting it again.
      window.location.assign(routes.cart_url);
      return;
    }
    this.classList.remove('is-empty');
    this.productId = parsedState.id;
    updates.forEach(({ target, html }) => { target.innerHTML = html; });

    this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
    if (shouldOpen) this.open();
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);
